import { useEffect } from 'react'

import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import type { Location } from 'history'
import { Helmet } from 'react-helmet-async'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { StyleSheetManager } from 'styled-components'

import {
  AnalyticsProvider,
  Cookies,
  ddLogger,
  ErrorBoundary,
  GTMConsentProvider,
  SnackbarContainer,
} from '@b-stock/fe-core/bstock-react'

import appConfig from '@config/config'
import CookieManager from '@helpers/CookieManager'
import { FeatureFlagProvider } from '@helpers/featureFlags/featureFlags'

import './initializeFA'
import '@helpers/validation'
import { gtag, gtagFields } from '@helpers/gtag'
import { useLogError } from '@helpers/logError'
import usePageTitle from '@helpers/usePageTitle'
import useUserInfo from '@helpers/useUserInfo'
import useAnalytics from '@pages/shared/segmentAnalytics/useAnalytics'
import { queryClient } from '@store/data'
import type { RootState } from '@store/rootReducer'

import BrazeContainer from './BrazeContainer'
import GlobalStyle from './GlobalStyle'
import { PusherTokenRefreshSetup } from './PusherTokenRefreshSetup'
import Routes from './routes/Routes'
import '@b-stock/fe-core/styles/global.css'
import 'react-toastify/dist/ReactToastify.min.css'

ddLogger.startLogger({
  datadogConfig: appConfig.datadog,
  level: appConfig.datadog.level,
  env: appConfig.env,
  enabled: appConfig.datadog.enabled,
  appName: appConfig.appName,
})

const isLowerEnvironment =
  process.env.NODE_ENV === 'development' ||
  ['dev', 'qa'].includes(appConfig.env)

/**
 * Component responsible for initializing user analytics and tracking.
 * Must be rendered within GTMConsentProvider to access consent context.
 */
const UserAnalytics = () => {
  const { identifyUser } = useAnalytics()
  const { accountId, personId } = useUserInfo()
  // react-router-dom's v5 types don't resolve under tsgolint, so this hook comes
  // back as an error type. Assert the `history` package's own type.
  const { pathname, search } = useLocation() as Location
  const { isLoggedIn } = useSelector(({ auth }: RootState) => auth.login)

  useEffect(() => {
    const uaId = CookieManager.getCookie(Cookies.BSTOCK_UA_ID)
    if (accountId && personId) {
      identifyUser(personId, accountId)
      ddLogger.setGlobalContext({ token: { accountId, personId } })
    }
    gtag({
      content_group: 'account',
      user_status: isLoggedIn
        ? gtagFields.user_status.logged_in
        : gtagFields.user_status.logged_out,
      user_id: uaId || null,
      page_type: `${pathname}${search}`,
    })
    gtag({
      event: gtagFields.event.page_view,
      platform: gtagFields.platform,
      page_type: `${pathname}${search}`,
      page_name: pathname,
    })
  }, [accountId, identifyUser, isLoggedIn, pathname, personId, search])

  // This component only handles side effects, no UI
  return null
}

const PageTitleSetter = () => {
  const { title, description } = usePageTitle()
  return (
    <Helmet>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
    </Helmet>
  )
}

const App = () => {
  const { accountId } = useUserInfo()
  const logError = useLogError()

  return (
    <GTMConsentProvider>
      <AnalyticsProvider
        segmentWriteKey={appConfig.segment.wKey}
        useGTMConsent={!appConfig.segment.bypassConsent}
      >
        <UserAnalytics />
        <FeatureFlagProvider
          accountId={accountId || null}
          clientSideId={appConfig.launchDarklyClientSideId}
        >
          <StyleSheetManager>
            <QueryClientProvider client={queryClient}>
              {isLowerEnvironment ? <ReactQueryDevtools /> : null}
              <PusherTokenRefreshSetup />
              <BrazeContainer>
                <ErrorBoundary onError={logError}>
                  <PageTitleSetter />
                  <GlobalStyle />
                  <Routes />
                  <SnackbarContainer />
                </ErrorBoundary>
              </BrazeContainer>
            </QueryClientProvider>
          </StyleSheetManager>
        </FeatureFlagProvider>
      </AnalyticsProvider>
    </GTMConsentProvider>
  )
}

export default App
